import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { apiService } from '../services/api'

interface Category {
  id: string
  name: string
  nameAr?: string
  description?: string
  descriptionAr?: string
  parentId?: string
  image?: string
  icon?: string
  color?: string
  isActive: boolean
  sortOrder: number
  itemCount: number
  defaultPriority?: number
  createdAt: Date
  updatedAt: Date
  children?: Category[]
}

interface CategoryContextType {
  categories: Category[]
  loading: boolean
  addCategory: (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt' | 'itemCount'>) => void
  updateCategory: (id: string, updates: Partial<Category>) => void
  deleteCategory: (id: string) => void
  getCategoryById: (id: string) => Category | undefined
  getCategoriesByParent: (parentId?: string) => Category[]
  getAllCategoriesFlat: () => Category[]
  refreshCategories: () => void
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined)

interface CategoryProviderProps {
  children: ReactNode
}

export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    setLoading(true)
    try {
      const response = await apiService.getCategories()
      if (response.success && response.data) {
        // Transform database format (snake_case) to frontend format (camelCase)
        const transformedCategories = (response.data as any[]).map((dbCategory: any) => ({
          id: dbCategory.id,
          name: dbCategory.name,
          nameAr: dbCategory.name_ar || undefined,
          description: dbCategory.description || undefined,
          descriptionAr: dbCategory.description_ar || undefined,
          parentId: dbCategory.parent_id || undefined,
          image: dbCategory.image || undefined,
          icon: dbCategory.icon || undefined,
          color: dbCategory.color || '#3B82F6',
          isActive: dbCategory.is_active ?? true,
          sortOrder: dbCategory.sort_order || 0,
          itemCount: dbCategory.item_count || 0,
          defaultPriority: dbCategory.default_priority || 1,
          createdAt: new Date(dbCategory.created_at),
          updatedAt: new Date(dbCategory.updated_at),
          children: []
        }))

        // Build hierarchy: group children under parents
        const categoryMap = new Map<string, Category>()
        const rootCategories: Category[] = []

        // First pass: create all categories
        transformedCategories.forEach(cat => {
          categoryMap.set(cat.id, cat)
        })

        // Second pass: build hierarchy
        transformedCategories.forEach(cat => {
          if (cat.parentId) {
            const parent = categoryMap.get(cat.parentId)
            if (parent) {
              if (!parent.children) {
                parent.children = []
              }
              parent.children.push(cat)
            }
          } else {
            rootCategories.push(cat)
          }
        })

        // Sort categories and their children
        const sortCategories = (cats: Category[]): Category[] => {
          return cats
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(cat => ({
              ...cat,
              children: cat.children ? sortCategories(cat.children) : undefined
            }))
        }

        setCategories(sortCategories(rootCategories))
      } else {
        console.error('Failed to load categories:', response.error)
        setCategories([])
      }
    } catch (error) {
      console.error('Error loading categories:', error)
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const addCategory = async (categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt' | 'itemCount'>) => {
    try {
      // Transform frontend format to database format
      const dbCategoryData: any = {
        name: categoryData.name,
        name_ar: categoryData.nameAr || null,
        description: categoryData.description || null,
        description_ar: categoryData.descriptionAr || null,
        parent_id: categoryData.parentId || null,
        image: categoryData.image || null,
        icon: categoryData.icon || null,
        color: categoryData.color || '#3B82F6',
        is_active: categoryData.isActive ?? true,
        sort_order: categoryData.sortOrder || 0,
        default_priority: categoryData.defaultPriority || 1
      }

      const response = await apiService.createCategory(dbCategoryData)
      if (response.success) {
        // Reload categories from database
        await loadCategories()
      } else {
        console.error('Failed to create category:', response.error)
        throw new Error(response.error || 'Failed to create category')
      }
    } catch (error) {
      console.error('Error creating category:', error)
      throw error
    }
  }

  const updateCategory = async (id: string, updates: Partial<Category>) => {
    try {
      // Transform frontend format to database format
      const dbUpdates: any = {}
      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.nameAr !== undefined) dbUpdates.name_ar = updates.nameAr || null
      if (updates.description !== undefined) dbUpdates.description = updates.description || null
      if (updates.descriptionAr !== undefined) dbUpdates.description_ar = updates.descriptionAr || null
      if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId || null
      if (updates.image !== undefined) dbUpdates.image = updates.image || null
      if (updates.icon !== undefined) dbUpdates.icon = updates.icon || null
      if (updates.color !== undefined) dbUpdates.color = updates.color
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive
      if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder
      if (updates.defaultPriority !== undefined) dbUpdates.default_priority = updates.defaultPriority

      const response = await apiService.updateCategory(id, dbUpdates)
      if (response.success) {
        // Reload categories from database
        await loadCategories()
      } else {
        console.error('Failed to update category:', response.error)
        throw new Error(response.error || 'Failed to update category')
      }
    } catch (error) {
      console.error('Error updating category:', error)
      throw error
    }
  }

  const deleteCategory = async (id: string) => {
    try {
      const response = await apiService.deleteCategory(id)
      if (response.success) {
        // Reload categories from database
        await loadCategories()
      } else {
        console.error('Failed to delete category:', response.error)
        throw new Error(response.error || 'Failed to delete category')
      }
    } catch (error) {
      console.error('Error deleting category:', error)
      throw error
    }
  }

  const getCategoryById = (id: string): Category | undefined => {
    for (const category of categories) {
      if (category.id === id) return category
      if (category.children) {
        const found = category.children.find(child => child.id === id)
        if (found) return found
      }
    }
    return undefined
  }

  const getCategoriesByParent = (parentId?: string): Category[] => {
    if (!parentId) {
      return categories.filter(cat => !cat.parentId)
    }
    
    const parent = categories.find(cat => cat.id === parentId)
    return parent?.children || []
  }

  const getAllCategoriesFlat = (): Category[] => {
    const flat: Category[] = []
    categories.forEach(category => {
      flat.push(category)
      if (category.children) {
        flat.push(...category.children)
      }
    })
    return flat
  }

  const refreshCategories = () => {
    loadCategories()
  }

  return (
    <CategoryContext.Provider value={{
      categories,
      loading,
      addCategory,
      updateCategory,
      deleteCategory,
      getCategoryById,
      getCategoriesByParent,
      getAllCategoriesFlat,
      refreshCategories
    }}>
      {children}
    </CategoryContext.Provider>
  )
}

export const useCategories = () => {
  const context = useContext(CategoryContext)
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoryProvider')
  }
  return context
}
